"use client";
import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { respondToLegalDocument, respondToLegalDocumentsBulk } from "@/services/api";
import styles from "./LegalConsentModal.module.scss";

interface PendingDoc {
  _id: string;
  type: "privacy" | "terms" | "security";
  title: string;
  content: string;
  version: number;
}

interface Props {
  docs: PendingDoc[];
  userRole: string;
  onDone: () => void;
}

const TYPE_ICON: Record<string, string> = {
  privacy: "🔒",
  terms: "📋",
  security: "🛡️",
};

function renderContent(text: string) {
  return text.split("\n").map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={i} className={styles.spacer} />;
    if (/^\d+\.\s+[A-Z]/.test(trimmed) || /^[A-Z][A-Z\s&—\-]+$/.test(trimmed)) {
      return <div key={i} className={styles.sectionHeading}>{trimmed}</div>;
    }
    return <div key={i} className={styles.bodyLine}>{trimmed}</div>;
  });
}

export default function LegalConsentModal({ docs, userRole, onDone }: Props) {
  const { t } = useLanguage();
  const [responding, setResponding] = useState(false);
  // Track which doc _ids the user has checked (intends to accept)
  // all docs checked by default — unchecking means "skip this one"
  const [checked, setChecked] = useState<string[]>(docs.map(d => d._id));
  // Track viewed doc for content display
  const [activeIndex, setActiveIndex] = useState(0);

  const canAccept = userRole === "ADMIN";
  const current = docs[activeIndex] ?? docs[0];

  const toggleCheck = (id: string) => {
    setChecked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleAccept = async () => {
    setResponding(true);
    try {
      const checkedDocs = docs.filter(d => checked.includes(d._id));
      const uncheckedDocs = docs.filter(d => !checked.includes(d._id));

      const requests: { documentId: string; action: "accepted" | "skipped" }[] = [
        ...checkedDocs.map(d => ({ documentId: d._id, action: "accepted" as const })),
        ...uncheckedDocs.map(d => ({ documentId: d._id, action: "skipped" as const })),
      ];

      try {
        await respondToLegalDocumentsBulk(requests);
      } catch {
        // fallback: individual calls if bulk endpoint not available
        for (const req of requests) {
          try { await respondToLegalDocument(req.documentId, req.action); } catch { /* ignore */ }
        }
      }
    } finally {
      setResponding(false);
      onDone();
    }
  };

  const handleSkipAll = async () => {
    setResponding(true);
    try {
      const requests = docs.map(d => ({ documentId: d._id, action: "skipped" as const }));
      try {
        await respondToLegalDocumentsBulk(requests);
      } catch {
        for (const d of docs) {
          try { await respondToLegalDocument(d._id, "skipped"); } catch { /* ignore */ }
        }
      }
    } finally {
      setResponding(false);
      onDone();
    }
  };

  if (!current) {
    onDone();
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.shieldIcon}>🔐</div>
            <div>
              <h2>{t("legal.policyUpdate")}</h2>
              <p>{t("legal.reviewMultiple")}</p>
            </div>
          </div>
        </div>

        {/* Doc tabs */}
        {docs.length > 1 && (
          <div className={styles.docTabs}>
            {docs.map((d, i) => (
              <button
                key={d._id}
                type="button"
                className={`${styles.docTab} ${i === activeIndex ? styles.docTabActive : ""} ${checked.includes(d._id) ? styles.docTabDone : ""}`}
                onClick={() => setActiveIndex(i)}
              >
                {checked.includes(d._id) ? "✓" : TYPE_ICON[d.type]} {d.type.charAt(0).toUpperCase() + d.type.slice(1)}
              </button>
            ))}
          </div>
        )}

        {/* Doc content */}
        <div className={styles.docHeader}>
          <span className={styles.typeIcon}>{TYPE_ICON[current.type]}</span>
          <div>
            <h3>{current.title}</h3>
            <span className={styles.versionTag}>{t("legal.version")} {current.version}</span>
          </div>
        </div>

        <div className={styles.contentArea}>
          {renderContent(current.content)}
        </div>

        {/* Checkboxes left · buttons right */}
        <div className={styles.actions}>
          {canAccept ? (
            <>
              <div className={styles.checkboxList}>
                {docs.map(d => (
                  <label key={d._id} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={checked.includes(d._id)}
                      onChange={() => toggleCheck(d._id)}
                      disabled={responding}
                      className={styles.checkbox}
                    />
                    <span className={styles.checkboxIcon}>{TYPE_ICON[d.type]}</span>
                    <span>{d.type.charAt(0).toUpperCase() + d.type.slice(1)}</span>
                  </label>
                ))}
              </div>
              <div className={styles.actionBtns}>
                <button
                  className={styles.skipBtn}
                  onClick={handleSkipAll}
                  disabled={responding}
                >
                  {t("legal.skip")}
                </button>
                <button
                  className={styles.acceptBtn}
                  onClick={handleAccept}
                  disabled={responding}
                >
                  {responding ? t("legal.saving") : `✓ ${t("legal.accept")}`}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className={styles.operatorNotice}>
                {t("legal.operatorNotice")}
              </div>
              <button
                className={styles.skipBtn}
                onClick={handleSkipAll}
                disabled={responding}
              >
                {responding ? "..." : t("legal.continue")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
